import {
  CallAdminLog,
  CallAdminLogStep,
  CallAdminLogType
} from "__generated__/graphql"

/**
 * Turns the flat `CallAdminLogs.logs[]` audit trail into the story of the call: where it
 * entered, which gates it passed, who was dialed, who declined, who answered and how it
 * ended. The raw step list stays below it in the drawer for everything this skips.
 *
 * Every mapping here is keyed on a `stepName` the phone driver actually emits — an
 * unrecognised step is ignored unless it failed, so a new step can never silently break
 * the summary, it just won't be narrated until it is added below.
 */

export type CallTimelineTone = "neutral" | "good" | "warn" | "bad" | "info"

export type CallTimelineIcon =
  | "inbound"
  | "outbound"
  | "team"
  | "gate"
  | "bot"
  | "ivr"
  | "queue"
  | "dial"
  | "answered"
  | "declined"
  | "missed"
  | "transfer"
  | "survey"
  | "record"
  | "hangup"
  | "end"
  | "error"

export type CallTimelineEvent = {
  id: string
  at: number
  offsetMs: number
  icon: CallTimelineIcon
  label: string
  detail?: string
  tone: CallTimelineTone
}

export type CallTimelineOutcome = {
  label: string
  tone: CallTimelineTone
}

export type CallTimelineSummary = {
  kind: "inbound" | "outbound" | "team" | "unknown"
  fromLabel: string
  toLabel: string
  personName?: string
  outcome?: CallTimelineOutcome
  totalDurationSec?: number
  talkSec?: number
  queueSec?: number
  cost?: number
  surveyScore?: string
  hasRecording: boolean
  hasTranscript: boolean
  /**
   * Public S3 urls of the call recordings, in the order they were uploaded. A transferred
   * call produces one per segment, so this is a list rather than a single url.
   */
  recordingUrls: string[]
  recordingDurationSec?: number
  agentsTried: number
  failedCount: number
}

export type CallTimeline = {
  summary: CallTimelineSummary
  events: CallTimelineEvent[]
}

/** `data` arrives through the `Object` GraphQL scalar, so nothing about it is typed. */
type StepData = Record<string, unknown>

function asRecord(value: unknown): StepData | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as StepData
}

function readString(data: StepData | null, key: string): string | undefined {
  const value = data?.[key]
  if (typeof value === "string" && value.trim()) return value
  if (typeof value === "number") return String(value)
  return undefined
}

function readNumber(data: StepData | null, key: string): number | undefined {
  const value = data?.[key]
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string") {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

function readBoolean(data: StepData | null, key: string): boolean | undefined {
  const value = data?.[key]
  return typeof value === "boolean" ? value : undefined
}

/** Webhook steps carry the Twilio POST body at `data.args[0].body`. */
function readWebhookBody(data: StepData | null): StepData | null {
  const args = data?.args
  if (!Array.isArray(args) || args.length === 0) return null
  return asRecord(asRecord(args[0])?.body)
}

const HISTORY_STATUS_TONES: Record<string, CallTimelineTone> = {
  answered: "good",
  completed: "good",
  in_progress: "info",
  abandoned: "warn",
  queue_abandoned: "warn",
  ivr_abandoned: "warn",
  no_answer: "bad",
  missed: "bad",
  declined: "bad",
  rejected: "bad",
  canceled: "warn",
  failed: "bad",
  unreachable: "bad"
}

const LEG_STATUS_LABELS: Record<
  string,
  { label: string; tone: CallTimelineTone }
> = {
  "no-answer": { label: "Cevaplamadı", tone: "bad" },
  busy: { label: "Meşgul / reddetti", tone: "bad" },
  canceled: { label: "Arama iptal edildi", tone: "warn" },
  failed: { label: "Bağlanamadı", tone: "bad" },
  "in-progress": { label: "Cevapladı", tone: "good" }
}

function outcomeFromHistoryStatus(status: string): CallTimelineOutcome {
  const normalized = status.toLowerCase().replace(/-/g, "_")
  return {
    label: status.toUpperCase(),
    tone: HISTORY_STATUS_TONES[normalized] ?? "neutral"
  }
}

export type CallTimelineInput = {
  log: CallAdminLog
  /** cPerson id -> display name, so the timeline reads as people rather than ObjectIds. */
  agentNames: Map<string, string>
}

export function buildCallTimeline(params: CallTimelineInput): CallTimeline {
  const steps: CallAdminLogStep[] = params.log.logs ?? []
  const events: CallTimelineEvent[] = []

  const summary: CallTimelineSummary = {
    kind: params.log.callType === CallAdminLogType.Team ? "team" : "unknown",
    fromLabel: "",
    toLabel: "",
    hasRecording: false,
    hasTranscript: false,
    recordingUrls: [],
    agentsTried: 0,
    failedCount: 0
  }

  const firstAt = steps.length > 0 ? Number(steps[0].timestamp ?? 0) : 0
  const dialedAgents = new Set<string>()
  const answeredAgents = new Set<string>()
  // A step can be narrated by more than one rule (a failure and its own mapping); this
  // keeps the generic failure catch-all from duplicating what a rule already said.
  let index = 0

  const nameOf = (id: string | undefined): string => {
    if (!id) return "bilinmeyen kullanıcı"
    return params.agentNames.get(id) ?? `#${id.slice(-6)}`
  }

  const push = (
    step: CallAdminLogStep,
    event: Omit<CallTimelineEvent, "id" | "at" | "offsetMs">
  ) => {
    const at = Number(step.timestamp ?? 0)
    events.push({
      ...event,
      id: `${index}-${step.stepName ?? "step"}`,
      at,
      offsetMs: firstAt > 0 ? Math.max(0, at - firstAt) : 0
    })
  }

  steps.forEach((step, stepIndex) => {
    index = stepIndex
    const name = String(step.stepName ?? "")
    const status = String(step.status ?? "")
    const data = asRecord(step.data)
    let narrated = false

    if (status === "failed") {
      summary.failedCount += 1
    }

    // ---- entry ------------------------------------------------------------
    if (name === "Handle Coming Call" && status === "started") {
      const body = readWebhookBody(data)
      summary.kind = "inbound"
      summary.fromLabel =
        readString(body, "Caller") ?? readString(body, "From") ?? ""
      summary.toLabel =
        readString(body, "Called") ?? readString(body, "To") ?? ""
      push(step, {
        icon: "inbound",
        tone: "info",
        label: "Gelen çağrı",
        detail: `${summary.fromLabel || "?"} → ${summary.toLabel || "?"}`
      })
      narrated = true
    }

    if (name === "Handle Going Call" && status === "started") {
      const body = readWebhookBody(data)
      summary.kind = "outbound"
      summary.fromLabel = readString(body, "From") ?? ""
      summary.toLabel =
        readString(body, "To") ?? readString(body, "Called") ?? ""
      push(step, {
        icon: "outbound",
        tone: "info",
        label: "Giden çağrı başlatıldı",
        detail: summary.toLabel || undefined
      })
      narrated = true
    }

    if (name === "TeamCall: Start" && status === "started") {
      summary.kind = "team"
      push(step, {
        icon: "team",
        tone: "info",
        label: "Ekip araması başlatıldı"
      })
      narrated = true
    }

    // ---- gates ------------------------------------------------------------
    if (name === "Audit: Check App Status") {
      const appStatus = readString(data, "appStatus")
      if (appStatus && appStatus !== "Enabled") {
        push(step, {
          icon: "gate",
          tone: "bad",
          label: "Hesap kapalı",
          detail: appStatus
        })
        narrated = true
      }
    }

    if (name === "Audit: Check Working Hours") {
      const outside = readBoolean(data, "outside")
      if (outside) {
        push(step, {
          icon: "gate",
          tone: status === "failed" ? "bad" : "warn",
          label: "Mesai saatleri dışında",
          detail:
            status === "failed"
              ? "Çağrı kabul edilmedi"
              : "Çağrı yine de kabul edildi"
        })
        narrated = true
      }
    }

    if (name === "Audit: Outside Work Hours Reject") {
      push(step, {
        icon: "gate",
        tone: "bad",
        label: "Mesai dışı: çağrı reddedildi"
      })
      narrated = true
    }

    if (name === "Audit: Check Product Balance" && status === "failed") {
      push(step, { icon: "gate", tone: "bad", label: "Bakiye yetersiz" })
      narrated = true
    }

    // ---- who is on the other end -----------------------------------------
    if (name === "Audit: Get Person Info") {
      summary.personName = readString(data, "firstName") ?? summary.personName
    }

    // ---- bot / IVR --------------------------------------------------------
    if (name === "Audit: FlowBot Handling" && readBoolean(data, "flow")) {
      push(step, { icon: "bot", tone: "info", label: "FlowBot devraldı" })
      narrated = true
    }

    if (name === "Audit: IVR Input Received") {
      push(step, {
        icon: "ivr",
        tone: "info",
        label: "IVR girişi",
        detail: readString(data, "userInput") ?? readString(data, "digit")
      })
      narrated = true
    }

    if (name === "Audit: IVR Timeout") {
      push(step, { icon: "ivr", tone: "warn", label: "IVR zaman aşımı" })
      narrated = true
    }

    // ---- queue ------------------------------------------------------------
    if (name === "Audit: Assign To Agent Start") {
      const totalTimeout = readNumber(data, "totalTimeout")
      push(step, {
        icon: "queue",
        tone: "info",
        label: "Kuyruğa alındı",
        detail: totalTimeout
          ? `Toplam bekleme sınırı ${totalTimeout} sn`
          : undefined
      })
      narrated = true
    }

    if (name === "Audit: Queue Session Created") {
      const agentCount = readNumber(data, "agentCount")
      push(step, {
        icon: "queue",
        tone: "info",
        label: "Kuyruk açıldı",
        detail:
          agentCount !== undefined ? `${agentCount} uygun agent` : undefined
      })
      narrated = true
    }

    if (name === "Audit: Queue Dispatch Agent") {
      const agentId = readString(data, "agentId")
      if (agentId) dialedAgents.add(agentId)
      push(step, {
        icon: "dial",
        tone: "neutral",
        label: "Agent arandı",
        detail: nameOf(agentId)
      })
      narrated = true
    }

    if (name === "Audit: Dialing Direct Agent") {
      const agentId = readString(data, "agentId")
      if (agentId) dialedAgents.add(agentId)
      push(step, {
        icon: "dial",
        tone: "neutral",
        label: "Agent arandı (kuyruksuz)",
        detail: nameOf(agentId)
      })
      narrated = true
    }

    if (name === "Audit: Assign To Agent Callback Branch Context") {
      const legStatus = readString(data, "normalizedStatus") ?? ""
      const mapped = LEG_STATUS_LABELS[legStatus]
      if (mapped) {
        const attempt = readNumber(data, "attemptCount")
        if (mapped.tone === "good") {
          const answeredId = readString(data, "agentId")
          if (answeredId) answeredAgents.add(answeredId)
        }
        push(step, {
          icon: mapped.tone === "good" ? "answered" : "declined",
          tone: mapped.tone,
          label: mapped.label,
          detail: [
            nameOf(readString(data, "agentId")),
            attempt ? `${attempt}. deneme` : ""
          ]
            .filter(Boolean)
            .join(" · ")
        })
        narrated = true
      }
    }

    if (name === "Audit: Assign To Agent Callback - Missed") {
      push(step, {
        icon: "missed",
        tone: "bad",
        label: "Cevapsız",
        detail: nameOf(readString(data, "agentId"))
      })
      narrated = true
    }

    if (name === "Audit: Assign To Agent Callback Completed") {
      const agentId = readString(data, "agentId")
      // The leg already reported `in-progress` for this agent — saying it twice adds
      // noise to a summary whose whole point is one glance.
      if (!agentId || !answeredAgents.has(agentId)) {
        push(step, {
          icon: "answered",
          tone: "good",
          label: "Agent bağlandı",
          detail: nameOf(agentId)
        })
      }
      narrated = true
    }

    if (
      name === "Audit: Get Random Agent" ||
      name === "Audit: Find Available Agent"
    ) {
      if (status === "failed") {
        push(step, {
          icon: "error",
          tone: "bad",
          label: "Uygun agent bulunamadı",
          detail: readString(data, "reason")
        })
        narrated = true
      }
    }

    if (name === "Audit: Find Available Agents") {
      const free = readNumber(data, "freeCount")
      const eligible = readNumber(data, "eligibleCount")
      if (free === 0) {
        push(step, {
          icon: "error",
          tone: "bad",
          label: "Boşta agent yok",
          detail:
            eligible !== undefined
              ? `${eligible} uygun agent meşgul`
              : undefined
        })
        narrated = true
      }
    }

    // ---- panel-initiated events -------------------------------------------
    if (name === "Audit: User Message Event") {
      const event = readString(data, "event")
      push(step, {
        icon: event === "TRANSFER" ? "transfer" : "hangup",
        tone: "info",
        label: "Panelden aksiyon",
        detail: event
      })
      narrated = true
    }

    if (name === "Audit: Hangup Call") {
      push(step, { icon: "hangup", tone: "neutral", label: "Çağrı kapatıldı" })
      narrated = true
    }

    // ---- team call --------------------------------------------------------
    if (name === "Audit: TeamCall Dialing") {
      const target = readString(data, "targetCPersonId")
      if (target) dialedAgents.add(target)
      summary.fromLabel =
        params.log.callerName ?? nameOf(readString(data, "callerCPersonId"))
      summary.toLabel = params.log.calleeName ?? nameOf(target)
      push(step, {
        icon: "dial",
        tone: "neutral",
        label: "Çalışma arkadaşı arandı",
        detail: summary.toLabel
      })
      narrated = true
    }

    if (name === "TeamCall: Leg Status" && status === "started") {
      const body = readWebhookBody(data)
      const legStatus = (readString(body, "CallStatus") ?? "").toLowerCase()
      const mapped = LEG_STATUS_LABELS[legStatus]
      const duration = readNumber(body, "CallDuration")

      if (mapped) {
        push(step, {
          icon: mapped.tone === "good" ? "answered" : "declined",
          tone: mapped.tone,
          label: mapped.label,
          detail: summary.toLabel || undefined
        })
        narrated = true
      } else if (legStatus === "completed") {
        if (duration !== undefined) summary.talkSec = duration
        push(step, {
          icon: "end",
          tone: "neutral",
          label: "Ekip araması sonlandı",
          detail: duration !== undefined ? `konuşma ${duration} sn` : undefined
        })
        narrated = true
      }
    }

    if (name.startsWith("Audit: TeamCall ") && status !== "success") {
      push(step, {
        icon: "error",
        tone: "bad",
        label: name.replace("Audit: TeamCall ", "Ekip araması: ")
      })
      narrated = true
    }

    // ---- survey / recording ----------------------------------------------
    if (name === "Audit: Survey Started") {
      push(step, { icon: "survey", tone: "info", label: "Anket başlatıldı" })
      narrated = true
    }

    if (name === "Audit: Survey Completed") {
      summary.surveyScore = readString(data, "score")
      push(step, {
        icon: "survey",
        tone: "good",
        label: "Anket tamamlandı",
        detail: summary.surveyScore ? `${summary.surveyScore} puan` : undefined
      })
      narrated = true
    }

    if (name === "Handle Recording Finished" && status === "started") {
      const body = readWebhookBody(data)
      summary.recordingDurationSec =
        readNumber(body, "RecordingDuration") ?? summary.recordingDurationSec
    }

    if (name === "Audit: Recording Uploaded to S3") {
      summary.hasRecording = true
      const location = readString(data, "location")
      if (location && !summary.recordingUrls.includes(location)) {
        summary.recordingUrls.push(location)
      }
      push(step, {
        icon: "record",
        tone: "neutral",
        label: "Kayıt yüklendi",
        detail:
          summary.recordingDurationSec !== undefined
            ? `${summary.recordingDurationSec} sn`
            : undefined
      })
      narrated = true
    }

    if (name === "Audit: Transcription") {
      summary.hasTranscript = true
      push(step, {
        icon: "record",
        tone: "neutral",
        label: "Transkript çıkarıldı"
      })
      narrated = true
    }

    // ---- money ------------------------------------------------------------
    if (name === "Audit: Price Calculation - Balance Charged") {
      summary.cost = readNumber(data, "chargeAmount") ?? summary.cost
    }

    // ---- terminal ---------------------------------------------------------
    if (name.includes("History Updated") || name === "Audit: Call Completed") {
      const historyStatus = readString(data, "historyStatus")
      const duration = readNumber(data, "duration")
      const talk =
        readNumber(data, "talkDuration") ?? readNumber(data, "inProgressTime")
      const queue = readNumber(data, "inQueueTime")

      if (historyStatus)
        summary.outcome = outcomeFromHistoryStatus(historyStatus)
      if (duration !== undefined) summary.totalDurationSec = duration
      if (talk !== undefined) summary.talkSec = talk
      if (queue !== undefined) summary.queueSec = queue

      push(step, {
        icon: "end",
        tone: summary.outcome?.tone ?? "neutral",
        label: "Çağrı sonlandı",
        detail: [
          historyStatus?.toUpperCase(),
          duration !== undefined ? `${duration} sn` : "",
          talk ? `konuşma ${talk} sn` : "",
          queue ? `kuyrukta ${queue} sn` : ""
        ]
          .filter(Boolean)
          .join(" · ")
      })
      narrated = true
    }

    if (name === "Handle Fallback" && status === "started") {
      push(step, {
        icon: "error",
        tone: "bad",
        label: "Fallback tetiklendi",
        detail: "Twilio ana webhook'a ulaşamadı"
      })
      narrated = true
    }

    // Anything that failed and no rule above explained — surfaced so a new or unexpected
    // failure is never invisible in the summary.
    if (!narrated && status === "failed") {
      push(step, {
        icon: "error",
        tone: "bad",
        label: name.replace(/^Audit: /, ""),
        detail: step.error ?? step.message ?? undefined
      })
    }
  })

  summary.agentsTried = dialedAgents.size

  if (!summary.fromLabel && params.log.callerNumber) {
    summary.fromLabel = params.log.callerNumber
  }
  if (!summary.outcome && params.log.status) {
    summary.outcome = outcomeFromHistoryStatus(params.log.status)
  }
  if (summary.totalDurationSec === undefined && params.log.totalDuration) {
    summary.totalDurationSec = params.log.totalDuration
  }

  return { summary, events }
}

/** Agent names the log itself already carries, so most calls need no extra lookup. */
export function collectAgentNamesFromLogs(
  log: CallAdminLog | null | undefined
): Map<string, string> {
  const names = new Map<string, string>()
  const steps: CallAdminLogStep[] = log?.logs ?? []

  steps.forEach((step) => {
    const data = asRecord(step.data)
    const randomAgent = asRecord(data?.randomAgent)
    const id = readString(randomAgent, "_id")
    const name = readString(randomAgent, "name")
    if (id && name && name !== "undefined undefined") {
      names.set(id, name)
    }

    const agentId = readString(data, "agentId")
    const email = readString(data, "email")
    if (agentId && email && !names.has(agentId)) {
      names.set(agentId, email)
    }
  })

  if (log?.callerCPersonId && log.callerName) {
    names.set(String(log.callerCPersonId), log.callerName)
  }
  if (log?.calleeCPersonId && log.calleeName) {
    names.set(String(log.calleeCPersonId), log.calleeName)
  }

  return names
}
